/* eslint-disable react-hooks/exhaustive-deps */
import type { ImapSettings, SmtpSettings } from 'src/types/checkout';

import { z as zod } from 'zod';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import React, { useMemo, useState, useCallback } from 'react';

import Grid from '@mui/material/Grid2';
import {
  Box,
  Card,
  Button,
  Switch,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useGoogleAuthPopup } from 'src/hook/useGoogleAuthPopup';
import { useMicroSoftAuthPopup } from 'src/hook/useMicroSoftAuthPopup';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';
import { LoadingScreen } from 'src/components/loading-screen';

import { useChannelContext } from './hooks';
import { AuthLoader } from '../auth/component/auth-loader';
import { useUpdateChannelMutation } from './slice/channelApi';
import { FacebookLoginButton } from './components/connect-whatsapp';
import { useGetAssigneeList } from '../tickets/hooks/useGetAssigneeList';
import { CustomAutocomplete } from '../tickets/components/custom-autocomplete';

function ChannelFormHandler({ selectedChannel, isLoading }: any) {
  const {
    state: channelState,
    handleGoogleDetail,
    handleMicrosoftDetail,
    handleFaceBookDeteail,
  } = useChannelContext();

  const [FacebookDetail, setFacebookDetail] = useState({});
  const isFacebookConnected = Boolean((FacebookDetail as { main_token?: string })?.main_token);

  const [updateChannel, { isLoading: isUpdateChannelLoading }] = useUpdateChannelMutation();

  const [redirectToScriptPage, setRedirectToScriptPage] = useState(false);
  const [liveChatData, setLiveChatData] = useState<any>(null);
  const { t } = useTranslate('translation');

  // all channel Edit field
  const fldList = useMemo(() => {
    const base = [
      { name: 'channelName', required: true },
      { name: 'assignedId', required: true },
    ];

    if (selectedChannel.channelType === 'EMAIL') {
      base.push({ name: 'email', required: true });
    }

    if (selectedChannel.channelType === 'WHATSAPP') {
      base.push({ name: 'pin', required: true });
      base.push({ name: 'pinAcknowledge', required: true });
    }

    if (
      selectedChannel?.channelType === 'EMAIL' &&
      selectedChannel?.EmailConfig?.[0]?.provider === 'other'
    ) {
      base.push(
        { name: 'smtpEnabled', required: false },
        { name: 'smtpAddress', required: true },
        { name: 'smtpPort', required: true },
        { name: 'smtpLogin', required: true },
        { name: 'smtpPassword', required: true },
        { name: 'smtpDomain', required: true },
        { name: 'imapEnabled', required: false },
        { name: 'imapAddress', required: true },
        { name: 'imapPort', required: true },
        { name: 'imapLogin', required: true },
        { name: 'imapPassword', required: true },
        { name: 'imapEnableSsl', required: false }
      );
    }

    if (selectedChannel?.channelType === 'LIVE_CHAT') {
      base.push(
        { name: 'websiteDomin', required: true },
        { name: 'widgetColor', required: false },
        { name: 'welcomeTitle', required: true },
        { name: 'welcomeTagline', required: true }
      );
    }
    return base;
  }, [selectedChannel]);

  // react-router
  const router = useRouter();

  type EditChannelSchemaType = {
    channelName: string;
    assignedId: any;
    pin?: string;
    pinAcknowledge?: boolean;
    email?: string;
    smtpEnabled?: boolean;
    smtpAddress?: string;
    smtpPort?: string;
    smtpLogin?: string;
    smtpPassword?: string;
    smtpDomain?: string;
    imapEnabled?: boolean;
    imapAddress?: string;
    imapPort?: number;
    imapLogin?: string;
    imapPassword?: string;
    imapEnableSsl?: boolean;
    websiteDomin?: string;
    widgetColor?: string;
    welcomeTitle?: string;
    welcomeTagline?: string;
  };

  const ChennalEditSchema = validationSchema(fldList, t).superRefine((data, ctx) => {
    const formData = data as SmtpSettings & ImapSettings & EditChannelSchemaType;

    if (!formData.channelName?.trim()) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['channelName'],
        message: 'Channel name is required',
      });
    }

    if (selectedChannel.channelType === 'WHATSAPP') {
      const trimmedPin = formData.pin?.trim() ?? '';

      if (isFacebookConnected) {
        if (!trimmedPin) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            path: ['pin'],
            message: '2FA PIN is required',
          });
        } else if (!/^\d{6}$/.test(trimmedPin)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            path: ['pin'],
            message: t('PIN_NUMERIC_ONLY'),
          });
        }

        if (formData.pinAcknowledge !== true) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            path: ['pinAcknowledge'],
            message: 'Please acknowledge the warnings',
          });
        }
      } else {
        // Clear out pin/pinAcknowledge if FB not connected to avoid validation issues elsewhere
        formData.pin = '';
        formData.pinAcknowledge = false;
      }
    }

    if (!formData.assignedId?.id?.trim()) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['assignedId'],
        message: 'Assigned ID is required',
      });
    }

    if (!formData.email?.trim() && selectedChannel.channelType === 'EMAIL') {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['email'],
        message: 'Email is required',
      });
    }

    if (selectedChannel.channelType === 'LIVE_CHAT') {
      if (!formData.websiteDomin?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['websiteDomin'],
          message: 'Website domain is required',
        });
      }
      if (!formData.welcomeTagline?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['welcomeTagline'],
          message: 'Wellcome tagline is required',
        });
      }
      if (!formData.welcomeTitle?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['welcomeTitle'],
          message: 'Wellcome title is required',
        });
      }
    }

    if (formData.smtpEnabled) {
      // ✅ Conditionally required if SMTP is enabled
      if (!formData.smtpAddress?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpAddress'],
          message: 'Address is required',
        });
      }

      if (!formData.smtpPort || formData.smtpPort <= 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpPort'],
          message: 'Port must be a positive number',
        });
      }

      if (!formData.smtpLogin?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpLogin'],
          message: 'Login is required',
        });
      }

      if (!formData.smtpPassword?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpPassword'],
          message: 'Password is required',
        });
      }

      if (!formData.smtpDomain?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpDomain'],
          message: 'Domain is required',
        });
      }
    }

    if (formData.imapEnabled) {
      // Check each field and add an error if it is invalid
      if (!formData.imapAddress.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapAddress'],
          message: 'Address is required',
        });
      }

      if (!formData.imapPort || formData.imapPort <= 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapPort'],
          message: 'Port must be a positive number',
        });
      }

      if (!formData.imapLogin.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapLogin'],
          message: 'Login is required',
        });
      }

      if (!formData.imapPassword.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapPassword'],
          message: 'Password is required',
        });
      }
    }
  });

  const defaultValues: EditChannelSchemaType = {
    channelName: '',
    assignedId: '',
    email: '',
  };

  const methods = useForm<EditChannelSchemaType>({
    mode: 'all',
    resolver: zodResolver(ChennalEditSchema),
    defaultValues,
    values: {
      ...selectedChannel,
      email: selectedChannel?.EmailConfig?.[0]?.email ?? '',
      pin:
        Array.isArray(selectedChannel?.WhatsAppConfig) && selectedChannel.WhatsAppConfig.length > 0
          ? (selectedChannel.WhatsAppConfig[0].pin ?? '')
          : '',
      pinAcknowledge: false,
      assignedId: { id: selectedChannel?.assignedId ?? '', name: '' },
      // SMTP Fields
      smtpEnabled: selectedChannel?.EmailConfig?.[0]?.smtpEnabled ?? false,
      smtpAddress: selectedChannel?.EmailConfig?.[0]?.smtpAddress ?? '',
      smtpPort: selectedChannel?.EmailConfig?.[0]?.smtpPort ?? '',
      smtpLogin: selectedChannel?.EmailConfig?.[0]?.smtpLogin ?? '',
      smtpPassword: selectedChannel?.EmailConfig?.[0]?.smtpPassword ?? '',
      smtpDomain: selectedChannel?.EmailConfig?.[0]?.smtpDomain ?? '',

      // IMAP Fields
      imapEnabled: selectedChannel?.EmailConfig?.[0]?.imapEnabled ?? false,
      imapAddress: selectedChannel?.EmailConfig?.[0]?.imapAddress ?? '',
      imapPort: selectedChannel?.EmailConfig?.[0]?.imapPort ?? '',
      imapLogin: selectedChannel?.EmailConfig?.[0]?.imapLogin ?? '',
      imapPassword: selectedChannel?.EmailConfig?.[0]?.imapPassword ?? '',
      imapEnableSsl: selectedChannel?.EmailConfig?.[0]?.imapEnableSsl ?? false,

      //live chat fields
      websiteName: '',
      websiteDomin: selectedChannel?.LiveChatConfig?.[0]?.domainUrl ?? '',
      widgetColor: selectedChannel?.LiveChatConfig?.[0]?.widgetColor ?? '',
      welcomeTitle: selectedChannel?.LiveChatConfig?.[0]?.welcomeTitle ?? '',
      welcomeTagline: selectedChannel?.LiveChatConfig?.[0]?.welcomeTagline ?? '',
    },
  });

  const { handleSubmit, setValue, control, watch } = methods;

  const {
    _recordList,
    searchValue,
    open,
    isFetching,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetAssigneeList({
    selectedTicket: selectedChannel,
    setValue: setValue as any,
    isDidPage: selectedChannel?.channelType === 'VOICE',
  });

  const redirectToRoot = () => {
    router.push(paths.channel.root);
  };

  const onSubmit = handleSubmit(async (data: Partial<any>) => {
    const getUpdatedPayload = (): Record<string, any> => {
      const channelPayload: Record<string, any> = {
        id: selectedChannel.id ?? '',
        channelName: data?.channelName ?? '',
        assignedType: data?.assignedId?.isUser ? 'USER' : 'TEAM',
        assignedId: data?.assignedId?.id ?? '',
      };

      if (selectedChannel.channelType === 'WHATSAPP') {
        const fbDetails = FacebookDetail as { main_token?: string };

        channelPayload.code = fbDetails?.main_token ?? ''; // set code even if empty

        if (isFacebookConnected) {
          // Save only if valid pin is entered
          if (data.pin && /^\d{6}$/.test(data.pin)) {
            channelPayload.pin = data.pin;
          }
        }
      }

      if (
        selectedChannel.channelType === 'EMAIL' &&
        selectedChannel?.EmailConfig[0]?.provider === 'google'
      ) {
        channelPayload.email = data.email;
        const googleDetails = channelState.googleEmailDetails as { code?: string };
        if (googleDetails?.code) {
          channelPayload.code = googleDetails.code;
        }
      }

      if (
        selectedChannel.channelType === 'EMAIL' &&
        selectedChannel.EmailConfig[0].provider === 'microsoft'
      ) {
        channelPayload.email = data.email;
        const microDetails = channelState.microsoftEmailDetails as { code?: string };
        if (microDetails?.code) {
          channelPayload.code = microDetails.code;
        }
      }

      if (
        selectedChannel.channelType === 'EMAIL' &&
        selectedChannel.EmailConfig[0].provider === 'other'
      ) {
        let defaultIMAPObj = {};
        let defaultSMTPObj = {};

        if (!data.imapEnabled) {
          defaultIMAPObj = {
            imapAddress: '',
            imapPort: 0,
            imapLogin: '',
            imapPassword: '',
            imapEnableSsl: true,
            imapEnabled: false,
          };
        }

        if (!data.smtpEnabled) {
          defaultSMTPObj = {
            smtpAddress: '',
            smtpPort: 0,
            smtpLogin: '',
            smtpPassword: '',
            smtpDomain: '',
            smtpEnabled: false,
          };
        }

        return {
          id: selectedChannel.id ?? '',
          assignedType: data?.assignedId?.isUser ? 'USER' : 'TEAM',
          ...data,
          ...defaultIMAPObj,
          ...defaultSMTPObj,
          assignedId: data?.assignedId?.id ?? '',
        };
      }

      if (selectedChannel.channelType === 'LIVE_CHAT') {
        channelPayload.domainUrl = data.websiteDomin;
        channelPayload.welcomeTagline = data.welcomeTagline;
        channelPayload.welcomeTitle = data.welcomeTitle;
        channelPayload.widgetColor = data.widgetColor;
      }
      return channelPayload;
    };

    try {
      if (Object.keys(data).length > 0) {
        try {
          const response = await updateChannel(getUpdatedPayload()).unwrap();
          setLiveChatData(response?.data || null);
          toast.success(t('CHANNEL_UPDATED_SUCCESSFULLY'));
          if (selectedChannel.channelType === 'LIVE_CHAT') {
            setRedirectToScriptPage(true);
          }
        } catch (err: any) {
          console.error('Error updating channel:', err);
          toast.error(t('CHANNEL_UPDATION_FAILED'));
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('CHANNEL_UPDATION_FAILED'));
    }

    // Clear the context state
    handleGoogleDetail('');
    handleMicrosoftDetail('');
    handleFaceBookDeteail('');

    // Redirect
    if (selectedChannel.channelType !== 'LIVE_CHAT') redirectToRoot();
  });

  const handleChange = ({ value, fieldName }: { value: string; fieldName: string }) => {};

  return (
    <Card sx={{ m: 2, px: 2, py: 3 }}>
      {isLoading ? (
        <Box>
          <LoadingScreen />
        </Box>
      ) : (
        <Form methods={methods} onSubmit={onSubmit} style={{ height: '100%' }}>
          <Card sx={{ height: '100%', p: 3 }}>
            {redirectToScriptPage ? (
              <CopyScriptPage
                scriptData={liveChatData?.config || null}
                redirectToRoot={redirectToRoot}
                t={t}
              />
            ) : (
              <Box
                sx={{
                  height: '90%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {isUpdateChannelLoading && <AuthLoader />}

                <Grid container spacing={3} justifyContent="space-between" width="90%">
                  <Box sx={{ my: 1, width: '100%' }}>
                    <Field.Text name="channelName" label={t('CHANNEL_NAME')} />
                  </Box>

                  {selectedChannel.channelType === 'LIVE_CHAT' && (
                    <LiveChatFields t={t} selectedChannel={selectedChannel} setValue={setValue} />
                  )}
                  <Box sx={{ my: 1, width: '100%' }}>
                    <CustomAutocomplete
                      name="assignedId"
                      label={t('ASSIGNED_TO')}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      placeholder="Select a Team"
                      options={_recordList}
                      searchValue={searchValue}
                      listScrollHandler={listScrollHandler}
                      searchValueHandler={searchValueHandler}
                      open={open}
                      openListHandler={openListHandler}
                      closeListHandler={closeListHandler}
                      isFetching={isFetching}
                      onChangeField={handleChange}
                    />
                  </Box>

                  {selectedChannel.channelType === 'WHATSAPP' && (
                    <WhatsappFields
                      t={t}
                      setFacebookDetail={setFacebookDetail}
                      FacebookDetail={FacebookDetail}
                    />
                  )}

                  {selectedChannel.channelType === 'EMAIL' &&
                    selectedChannel.EmailConfig.length > 0 &&
                    selectedChannel.EmailConfig[0].provider === 'google' && (
                      <GoogleFields selectedChannel={selectedChannel} t={t} />
                    )}

                  {selectedChannel.channelType === 'EMAIL' &&
                    selectedChannel.EmailConfig.length > 0 &&
                    selectedChannel.EmailConfig[0].provider === 'microsoft' && (
                      <MicrosoftFields selectedChannel={selectedChannel} t={t} />
                    )}

                  {selectedChannel.channelType === 'EMAIL' &&
                    selectedChannel.EmailConfig.length > 0 &&
                    selectedChannel.EmailConfig[0].provider === 'other' && (
                      <OtherProviderFields
                        selectedChannel={selectedChannel}
                        control={control}
                        t={t}
                        watch={watch}
                      />
                    )}

                  {selectedChannel.channelType === 'VOICE' &&
                    selectedChannel.VoiceConfig.length > 0 && (
                      <DidFields selectedChannel={selectedChannel} />
                    )}
                </Grid>
                <Box sx={{ flexGrow: 1 }} />
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    width: '90%',
                  }}
                >
                  <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                    {t('SAVE')}
                  </Button>
                </Box>
              </Box>
            )}
          </Card>
        </Form>
      )}
    </Card>
  );
}

export default ChannelFormHandler;

const WhatsappFields = ({ setFacebookDetail, setDisable, t, FacebookDetail }: any) => {
  const show2FAPassword = useBoolean();

  const handleFaceBookDeteail = useCallback((value: any) => {
    setFacebookDetail(value);
  }, []);

  return (
    <Box sx={{ my: 1 }}>
      <FacebookLoginButton handleFaceBookDeteail={handleFaceBookDeteail} />

      {FacebookDetail?.main_token && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Field.Text
            name="pin"
            label={t('2FA_PIN')}
            type={show2FAPassword.value ? 'text' : 'password'}
            placeholder={t('SET_6_DIGIT_PIN')}
            slotProps={{
              input: {
                inputProps: {
                  pattern: '[0-9]*',
                  inputMode: 'numeric',
                  maxLength: 6,
                  autoComplete: 'new-password',
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={show2FAPassword.value ? t('HIDE') : t('SHOW')}>
                      <IconButton onClick={show2FAPassword.onToggle} edge="end">
                        <Iconify
                          icon={show2FAPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
              inputLabel: { shrink: true },
            }}
            helperText={t('PIN_HELP_TEXT')}
          />

          <Box
            sx={{
              mt: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Iconify
                    icon="eva:checkmark-circle-2-fill"
                    sx={{
                      color: 'error.main',
                      fontSize: 16,
                      mt: 0.25,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'error.main',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {t(`PIN_WARNING_${i}`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Field.Checkbox name="pinAcknowledge" label={t('PIN_ACKNOWLEDGEMENT')} />
        </Box>
      )}
    </Box>
  );
};

const GoogleFields = ({ selectedChannel, t }: any) => {
  const { loginWithGoogle } = useGoogleAuthPopup({
    Provider: selectedChannel.EmailConfig[0].provider ?? '',
  });

  const signInBtnHandler = () => {
    loginWithGoogle();
  };

  return (
    <>
      <Box sx={{ my: 1, width: '100%' }}>
        <Field.Text name="email" label={t('EMAIL')} />
      </Box>
      <Button variant="contained" sx={{ background: '#50a28a', my: 2 }} onClick={signInBtnHandler}>
        Login with Google
      </Button>
    </>
  );
};

const MicrosoftFields = ({ selectedChannel, t }: any) => {
  const { loginWithMicrosoft } = useMicroSoftAuthPopup({
    Provider: selectedChannel.EmailConfig[0].provider ?? '',
    id: selectedChannel.id,
  });

  return (
    <>
      <Box sx={{ my: 1, width: '100%' }}>
        <Field.Text name="email" label={t('EMAIL')} />
      </Box>
      <Button
        variant="contained"
        sx={{ background: '#50a28a', my: 2 }}
        onClick={() => loginWithMicrosoft()}
      >
        Login with Microsoft
      </Button>
    </>
  );
};

const OtherProviderFields = ({ selectedChannel, control, t, watch }: any) => {
  const [watchSmtpEnabled, watchImapEnabled] = watch(['smtpEnabled', 'imapEnabled']);
  const showPassword = useBoolean();
  const showsmtpPassword = useBoolean();

  return (
    <>
      <Box sx={{ my: 1, width: '100%' }}>
        <Field.Text name="email" label={t('EMAIL')} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, ml: 8 }}>
            <Typography variant="subtitle1">SMTP</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sent your SMTP Details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '60%' }}>
            <FormControlLabel
              control={
                <Controller
                  name="smtpEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      color="primary"
                      checked={field.value} // Bind the checked state to the form value
                      onChange={(e) => field.onChange(e.target.checked)} // Update the form value on change
                    />
                  )}
                />
              }
              label={
                <Typography variant="subtitle1">
                  Enable SMTP Configuration for this Inbox
                </Typography>
              }
            />

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enabling SMTP will help the user to send email
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: '60%', alignSelf: 'end' }}>
          {watchSmtpEnabled && ( // Only show fields if smtpEnabled is true
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Field.Text
                name="smtpAddress"
                label={t('SMTP_ADDRESS')}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <Field.Text
                name="smtpPort"
                label={t('SMTP_PORT')}
                type="number"
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <Field.Text
                name="smtpLogin"
                label={t('SMTP_LOGIN')}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <Field.Text
                name="smtpPassword"
                label={t('SMTP_PASSWORD')}
                type={showsmtpPassword.value ? 'text' : 'password'}
                autoComplete="off"
                slotProps={{
                  inputLabel: { shrink: true },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            showsmtpPassword.value
                              ? `${t('HIDE_PASSWORD')}`
                              : `${t('SHOW_PASSWORD')}`
                          }
                        >
                          <IconButton onClick={showsmtpPassword.onToggle} edge="end">
                            <Iconify
                              icon={
                                showsmtpPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'
                              }
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: { autoComplete: 'off' },
                }}
              />

              <Field.Text
                name="smtpDomain"
                label={t('SMTP_DOMAIN')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          )}
        </Box>
        {/* IMAP */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, ml: 8 }}>
            <Typography variant="subtitle1">IMAP</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sent your IMAP Details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '60%' }}>
            <FormControlLabel
              control={
                <Controller
                  name="imapEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      color="primary"
                      checked={field.value} // Bind the checked state to the form value
                      onChange={(e) => field.onChange(e.target.checked)} // Update the form value on change
                    />
                  )}
                />
              }
              label={
                <Typography variant="subtitle1">
                  Enable IMAP Configuration for this Inbox
                </Typography>
              }
            />

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enabling IMAP will help the user to receive email
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '60%', alignSelf: 'end' }}>
          {watchImapEnabled && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Field.Text
                name="imapAddress"
                label={t('IMAP_ADDRESS')}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <Field.Text
                name="imapPort"
                label={t('IMAP_PORT')}
                type="number"
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <Field.Text
                name="imapLogin"
                label={t('IMAP_LOGIN')}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              <Field.Text
                name="imapPassword"
                label={t('IMAP_PASSWORD')}
                type={showPassword.value ? 'text' : 'password'}
                autoComplete="off"
                slotProps={{
                  inputLabel: { shrink: true },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            showPassword.value ? `${t('HIDE_PASSWORD')}` : `${t('SHOW_PASSWORD')}`
                          }
                        >
                          <IconButton onClick={showPassword.onToggle} edge="end">
                            <Iconify
                              icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: { autoComplete: 'off' },
                }}
              />

              <FormControlLabel
                control={
                  <Controller
                    name="imapEnableSsl"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        color="primary"
                        checked={field.value} // Bind the checked state to the form value
                        onChange={(e) => field.onChange(e.target.checked)} // Update the form value on change
                      />
                    )}
                  />
                }
                label={<Typography variant="body1">{t('IMAP_ENABLE_SSL')}</Typography>}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const LiveChatFields = ({ t, selectedChannel, setValue }: any) => {
  const [color, setColor] = useState<string>(
    selectedChannel?.LiveChatConfig?.[0]?.widgetColor || '#000000'
  );
  return (
    <>
      {/* <Field.Text
          name="websiteName"
          label={t('WEB_NAME')}
          slotProps={{ inputLabel: { shrink: true } }}
          placeholder="Enter your website name"
        /> */}
      <Field.Text
        name="websiteDomin" // Fixed typo
        label={t('WEB_DOMAIN')}
        slotProps={{ inputLabel: { shrink: true } }}
        placeholder="Enter your website domain"
      />
      <Field.Text
        name="widgetColor"
        label={t('WIDGET_COLOR')}
        sx={{ width: '30%' }}
        slotProps={{ inputLabel: { shrink: true } }}
        type="color"
        value={color}
        onChange={(e) => {
          const newColor = e.target.value || '#000000';
          setColor(newColor);
          setValue('widgetColor', newColor);
        }}
      />
      <Field.Text
        name="welcomeTitle"
        label={t('WELL_HEADING')}
        slotProps={{ inputLabel: { shrink: true } }}
        placeholder="Hey there! 👋"
      />
      <Field.Text
        name="welcomeTagline"
        label={t('WELL_TAGLINE')}
        slotProps={{ inputLabel: { shrink: true } }}
        placeholder="Welcome! How can we assist you today?"
      />
    </>
  );
};

const DidFields = ({ selectedChannel }: any) => (
  <TextField
    label="DID Number"
    value={selectedChannel?.VoiceConfig[0].didNum ?? ''}
    disabled
    fullWidth
    margin="normal"
    sx={{ mt: 1, mb: 3 }}
  />
);

const CopyScriptPage = ({ scriptData, redirectToRoot, t }: any) => {
  const codeString = useMemo(() => {
    const rawScript = `<script>
    (function(d, t) {
      var g = d.createElement(t),
          s = d.getElementsByTagName(t)[0];
      g.src = '${window.location.origin}/sdk/chat-agent.js';
      g.defer = true;
      g.async = true;
      s.parentNode.insertBefore(g, s);
      g.onload = function() {
        window.liveChatSDK.run({
          tenantId: "${scriptData.tenantId}",
          websiteToken: "${scriptData.websiteToken}",
          welcomeTitle: "${scriptData.welcomeTitle}",
          welcomeTagline: "${scriptData.welcomeTagline}",
          widgetColor: "${scriptData.widgetColor}"
        });
      };
    })(document, "script");
  </script>`;

    // Encode the full script using Base64
    const encoded = btoa(unescape(encodeURIComponent(rawScript)));

    // Inject decode + execution logic
    return `<script>
    (function(){
      const script = decodeURIComponent(escape(atob("${encoded}")));
      document.write(script);
    })();
  </script>`;
  }, [scriptData]);

  const copyText = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  const copyTextHandler = () => {
    copyText(codeString);
    toast.success(t('SCRIPT_COPIED_SUCCESS'));
  };

  if (!scriptData) return '';
  return (
    <Box
      sx={{
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        p: 8,
      }}
    >
      <Box sx={{ width: '60%', textAlign: 'center', margin: '0 auto', my: 5 }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 3 }}>
          {t('CHANNEL_CREATED_SUCCESSFULLY')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {t('LIVE_CHAT_MESSAGE')}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflowX: 'auto', width: '800px' }}>
        <Typography variant="body1" sx={{ color: 'text.disabled' }}>
          <Box
            sx={{
              position: 'relative',
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '8px',
              overflowX: 'auto',
              fontSize: '0.75rem',
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={copyTextHandler}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                fontSize: '0.7rem',
                px: 1.5,
                py: 0.25,
                textTransform: 'uppercase',
              }}
            >
              {t('COPY')}
            </Button>
            <pre
              style={{
                margin: 0,
                width: '100%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <code>{codeString}</code>
            </pre>
          </Box>
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '90%',
        }}
      >
        <Button
          onClick={() => {
            redirectToRoot();
          }}
          variant="contained"
          sx={{ ml: 2 }}
        >
          {t('FINISH')}
        </Button>
      </Box>
    </Box>
  );
};
